import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plan } from '../types/plan';
import PlanSelectionPage from './PlanSelectionPage';
import { useAuth } from '../contexts/AuthContext';
import { changePlan } from '../utils/api';
import { organizationApi } from '../utils/organizationApi';
import { getPlanById } from '../utils/planApi';

const ChangePlanPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { currentOrganization, refreshCurrentOrganization, user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Verificar se é uma seleção forçada (usuário sem plano) ou alteração de plano
    const forceSelection = location.state?.forceSelection || false;
    const isPlanChange = !forceSelection && (location.state?.isPlanChange || true); // Se for seleção forçada, não é alteração
    const currentPlanId = currentOrganization?.currentPlan;
    const subscriptionActive = location.state?.subscriptionActive ?? currentOrganization?.subscriptionActive ?? true;

    const handlePlanSelect = async (plan: Plan) => {
        console.log('[ChangePlanPage] handlePlanSelect chamado');
        console.log('[ChangePlanPage] Plano selecionado:', plan);
        console.log('[ChangePlanPage] forceSelection:', forceSelection);
        console.log('[ChangePlanPage] Plano duration:', plan.duration);
        
        // Se o plano for gratuito, ativar diretamente sem processar pagamento
        if (plan.duration === 'free') {
            console.log('[ChangePlanPage] Plano gratuito detectado - ativando diretamente');
            setLoading(true);
            setError(null);
            
            try {
                // Se temos dados da organização, usar changePlan com dados válidos
                if (currentOrganization) {
                    // Usar email do usuário se disponível, senão usar email padrão válido
                    const customerEmail = user?.email || 'cliente@example.com';
                    const customerName = user?.name || currentOrganization.name || 'Cliente';
                    
                    const changePlanData: any = {
                        planId: plan.id,
                        customerName: customerName,
                        customerEmail: customerEmail, // Email válido necessário
                        customerIdentity: '00000000000', // CPF válido necessário
                        customerIdentityType: 'CPF'
                        // Não enviar dados de cartão para planos gratuitos
                    };
                    
                    const response = await changePlan(changePlanData);
                    
                    if (response.success) {
                        console.log('[ChangePlanPage] Plano gratuito ativado com sucesso');
                        
                        // Atualizar organização no contexto
                        if (response.organization) {
                            localStorage.setItem('currentOrganization', JSON.stringify(response.organization));
                        }
                        
                        // Atualizar organização no contexto
                        await refreshCurrentOrganization();
                        
                        // Remover flag de necessidade de seleção de plano
                        localStorage.removeItem('needsPlanSelection');
                        
                        // Redirecionar
                        if (forceSelection) {
                            navigate('/admin', {
                                state: {
                                    message: 'Plano selecionado com sucesso!',
                                    planChanged: true,
                                    showWelcome: true
                                },
                                replace: true
                            });
                        } else {
                            navigate('/admin/financeiro', {
                                state: {
                                    message: 'Plano alterado com sucesso!',
                                    planChanged: true
                                }
                            });
                        }
                    } else {
                        throw new Error(response.message || 'Erro ao ativar plano gratuito');
                    }
                } else {
                    throw new Error('Organização não encontrada');
                }
            } catch (err: any) {
                console.error('[ChangePlanPage] Erro ao ativar plano gratuito:', err);
                setError(err.message || 'Erro ao ativar plano gratuito. Tente novamente.');
                setLoading(false);
            }
            return;
        }
        
        // Para planos pagos, o PlanSelectionPage já trata o fluxo de pagamento
        // Se foi seleção forçada, remover a flag e redirecionar para admin
        if (forceSelection) {
            console.log('[ChangePlanPage] Redirecionando para /admin (forceSelection)');
            localStorage.removeItem('needsPlanSelection');
            // Após pagamento aprovado, redirecionar para admin
            navigate('/admin', {
                state: {
                    message: 'Plano selecionado com sucesso!',
                    planChanged: true,
                    showWelcome: true
                },
                replace: true
            });
        } else {
            console.log('[ChangePlanPage] Redirecionando para /admin/financeiro (isPlanChange)');
            // Após pagamento aprovado, voltar para a página financeira
            navigate('/admin/financeiro', {
                state: {
                    message: 'Plano alterado com sucesso!',
                    planChanged: true
                }
            });
        }
    };

    const handleBack = () => {
        // Se for seleção forçada, não permitir voltar (deve selecionar um plano)
        if (forceSelection) {
            return;
        }
        navigate('/admin/financeiro');
    };

    return (
        <div style={{ backgroundColor: '#f2f2f2', minHeight: '100vh' }}>
            {error && (
                <div style={{ 
                    position: 'fixed', 
                    top: 20, 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    zIndex: 9999,
                    padding: '12px 24px',
                    backgroundColor: '#ff4444',
                    color: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                    {error}
                </div>
            )}
            {loading && (
                <div style={{ 
                    position: 'fixed', 
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '24px',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '16px' }}>Ativando plano gratuito...</div>
                    </div>
                </div>
            )}
            <PlanSelectionPage
                onPlanSelect={handlePlanSelect}
                onBack={handleBack}
                isPlanChange={isPlanChange}
                forceSelection={forceSelection}
                currentPlanId={currentPlanId}
                subscriptionActive={subscriptionActive}
            />
        </div>
    );
};

export default ChangePlanPage;
